<template>
  <div>
    <div class="oct-salon-customer-list">
      <!-- Header -->
      <div class="oct-salon-customer-list-header">
        <!-- Title -->
        <h2 class="oct-salon-customer-list-header__title">
          {{ $data.$_octSCL_customers.length }}件
        </h2>

        <!-- Search and Filters -->
        <form
          class="oct-salon-customer-list-header__filters"
          @submit.prevent="$_octSCL_searchByCustomerName"
        >
          <!-- Search -->
          <oct-text-field
            v-model="$data.$_octSCL_searchWord"
            dense
            block
          >
            <template #leading-icon>
              <oct-icon icon="search" />
            </template>
          </oct-text-field>

          <!-- Limit -->
          <oct-select
            outline
            v-model="$data.$_octSCL_limit"
            @change.native="$_octSCL_update"
            :items="[
              {
                value: 20,
                text: '20件'
              },
              {
                value: 50,
                text: '50件'
              },
              {
                value: 100,
                text: '100件'
              },
              {
                value: 250,
                text: '250件'
              }
            ]"
          />
        </form>
      </div>

      <div class="oct-salon-customer-list__wrapper">
        <!-- Scrollable Container -->
        <div
          ref="octSclScrollContainer"
          class="oct-salon-customer-list__container"
          :class="{
            'oct-salon-customer-list__container--has-horizontal-overflow': $data.$_horizontalOverflow_isOverflowing,
          }"
        >
          <!-- Header Row -->
          <div class="oct-salon-customer-list__header oct-salon-customer-list__row">
            <div class="oct-salon-customer-list__col">
              &nbsp;
            </div>

            <div class="oct-salon-customer-list__col oct-salon-customer-list__col--header">
              氏名
            </div>

            <div
              v-for="(col, idx) in $_octSCL_headerColumns"
              :key="col.sortKey || `plain-${idx}`"
              class="oct-salon-customer-list__col oct-salon-customer-list__col--header"
            >
              <template v-if="!col.sortKey">
                {{ col.label }}
              </template>

              <span
                v-else
                class="oct-salon-customer-list__header-label"
              >
                {{ col.label }}

                <oct-popover
                  :anchorOrigin="$_octSCL_popoverAnchorOrigin(col)"
                  :transformOrigin="$_octSCL_popoverTransformOrigin(col)"
                  :closeOnPopperClick="true"
                >
                  <oct-icon
                    icon="filter"
                    class="oct-salon-customer-list__filter-icon"
                  />

                  <template #popper>
                    <oct-menu
                      class="oct-salon-customer-list__menu"
                      :class="{ 'oct-salon-customer-list__menu--popover-left': col.menuPlacement === 'left' }"
                    >
                      <oct-menu-item-group title="並び替え">
                        <oct-menu-list>
                          <oct-menu-item
                            @click.native="$_octSCL_setSort(col.sortKey, 'asc')"
                          >
                            <template #leadingIcon>
                              <oct-icon
                                v-if="$data.$_octSCL_sort === col.sortKey && $data.$_octSCL_order === 'asc'"
                                icon="done"
                              />
                              <span v-else>&nbsp;</span>
                            </template>

                            昇順で並び替え
                          </oct-menu-item>

                          <oct-menu-item
                            @click.native="$_octSCL_setSort(col.sortKey, 'desc')"
                          >
                            <template #leadingIcon>
                              <oct-icon
                                v-if="$data.$_octSCL_sort === col.sortKey && $data.$_octSCL_order === 'desc'"
                                icon="done"
                              />
                              <span v-else>&nbsp;</span>
                            </template>

                            降順で並び替え
                          </oct-menu-item>
                        </oct-menu-list>
                      </oct-menu-item-group>

                      <oct-menu-item role="separator" />

                      <oct-menu-item
                        :disabled="$_octSCL_isSortResetDisabled(col.sortKey)"
                        @click.native="$_octSCL_resetCustomerSort"
                      >
                        <template #leadingIcon>
                          &nbsp;
                        </template>

                        リセット
                      </oct-menu-item>
                    </oct-menu>
                  </template>
                </oct-popover>
              </span>
            </div>
          </div>

          <div
            v-if="!$data.$_octSCL_processing && $data.$_octSCL_customers.length > 0"
            class="oct-salon-customer-list__item"
          >
            <!-- Customer and Treatment History Rows -->
            <div
              v-for="item in $data.$_octSCL_customers"
              :key="item.customerId"
            >
              <!-- Customer Row -->
              <div
                @click="$_octSCL_onClickCustomer(item)"
                class="oct-salon-customer-list__row oct-salon-customer-list__row--customer"
              >
                <!-- Handle -->
                <div class="oct-salon-customer-list__col">
                  <svg
                    v-if="!item.expanded"
                    class="oct-salon-customer-list__handle"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path d="M14 7.25H8.75V2H7.25V7.25H2V8.75H7.25V14H8.75V8.75H14V7.25Z" fill="black"/>
                  </svg>

                  <svg
                    v-else
                    class="oct-salon-customer-list__handle"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                  >
                    <path d="M14 7.25V8.75H2V7.25H14Z"/>
                  </svg>
                </div>

                <!-- Customer Name -->
                <div class="oct-salon-customer-list__col">
                  <span class="oct-salon-customer-list__text">
                    {{ item.name || item.customerId }}
                  </span>
                </div>

                <!-- Stylist names -->
                <div class="oct-salon-customer-list__col">
                  <span class="oct-salon-customer-list__text">
                    {{ item.stylistNames.join(', ') }}
                  </span>
                </div>

                <!-- Last visited date -->
                <div class="oct-salon-customer-list__col">
                  {{ item.lastVisitedAt.toLocaleDateString() }}
                </div>

                <!-- Has purchased -->
                <div class="oct-salon-customer-list__col">
                  <span
                    class="oct-salon-customer-list__icon"
                    :class="{
                      'oct-salon-customer-list__icon--outlined': !item.hasPurchased,
                    }"
                  >
                    {{ item.hasPurchased ? '購入済' : '未購入' }}
                  </span>
                </div>

                <!-- Sales amount -->
                <div class="oct-salon-customer-list__col">
                  <span class="oct-salon-customer-list__text">
                    {{ $_octSCL_formatSalesAmount(item.performance.salesAmount) }}
                  </span>
                </div>

                <!-- Sent style count -->
                <div class="oct-salon-customer-list__col">
                  <span class="oct-salon-customer-list__text">
                    {{ item.performance.sentStyleCount }}
                  </span>
                </div>

                <!-- Viewed style count -->
                <div class="oct-salon-customer-list__col">
                  <span class="oct-salon-customer-list__text">
                    {{ item.performance.viewedStyleCount }}
                  </span>
                </div>

                <!-- Favorite count -->
                <div class="oct-salon-customer-list__col">
                  <span class="oct-salon-customer-list__text">
                    {{ item.performance.favoriteCount }}
                  </span>
                </div>

                <!-- Add to cart count -->
                <div class="oct-salon-customer-list__col">
                  <span class="oct-salon-customer-list__text">
                    {{ item.performance.addToCartCount }}
                  </span>
                </div>

                <!-- Purchase count -->
                <div class="oct-salon-customer-list__col">
                  <span class="oct-salon-customer-list__text">
                    {{ item.performance.purchaseCount }}
                  </span>
                </div>
              </div>

              <!-- Treatment History Rows -->
              <template v-if="item.expanded">
                <div
                  v-for="(history, index) in item.history"
                  :key="index"
                >
                  <div class="oct-salon-customer-list__row oct-salon-customer-list__row--treatment">
                    <div class="oct-salon-customer-list__col">
                      &nbsp;
                    </div>

                    <div class="oct-salon-customer-list__col">
                      &nbsp;
                    </div>

                    <!-- Stylist Names -->
                    <div class="oct-salon-customer-list__col">
                      <span class="oct-salon-customer-list__text">
                        {{ history.stylistNames.join(', ') }}
                      </span>
                    </div>

                    <!-- Sent At -->
                    <div class="oct-salon-customer-list__col">
                      {{ history.sentAt.toLocaleDateString() }}
                    </div>

                    <!-- Has Purchased -->
                    <div class="oct-salon-customer-list__col">
                      <span
                        class="oct-salon-customer-list__icon"
                        :class="{
                          'oct-salon-customer-list__icon--outlined': !history.hasPurchased,
                        }"
                      >
                        {{ history.hasPurchased ? '購入済' : '未購入' }}
                      </span>
                    </div>

                    <!-- Sales Amount -->
                    <div class="oct-salon-customer-list__col">
                      <span class="oct-salon-customer-list__text">
                        {{ history.salesAmount ? $_octSCL_formatSalesAmount(history.salesAmount) : '-' }}
                      </span>
                    </div>

                    <div class="oct-salon-customer-list__col">
                      <span class="oct-salon-customer-list__text">
                        –
                      </span>
                    </div>

                    <!-- Viewed -->
                    <div class="oct-salon-customer-list__col">
                      <span class="oct-salon-customer-list__icon"
                        :class="{
                          'oct-salon-customer-list__icon--outlined': !history.viewed,
                        }"
                      >
                        {{ history.viewed ? '閲覧' : '–' }}
                      </span>
                    </div>

                    <!-- Favorited -->
                    <div class="oct-salon-customer-list__col">
                      <span class="oct-salon-customer-list__icon"
                        :class="{
                          'oct-salon-customer-list__icon--outlined': !history.favorited,
                        }"
                      >
                        <svg
                          v-if="history.favorited"
                          class="oct-salon-customer-list__icon-favorite"
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path d="M8.625 0.75C7.3725 0.75 6.525 1.28625 6 1.7925C5.475 1.28625 4.6275 0.75 3.375 0.75C1.515 0.75 0 2.265 0 4.125C0 5.025 0.3525 5.8725 0.98625 6.51L5.4675 11.0287C5.61 11.1713 5.80125 11.25 6 11.25C6.19875 11.25 6.39 11.1713 6.5325 11.0287L11.0325 6.49125C11.655 5.85375 12 5.01375 12 4.125C12 2.265 10.485 0.75 8.625 0.75Z" fill="white"/>
                        </svg>

                        <span v-else>–</span>
                      </span>
                    </div>

                    <!-- Added To Cart -->
                    <div class="oct-salon-customer-list__col">
                      <span class="oct-salon-customer-list__icon"
                        :class="{
                          'oct-salon-customer-list__icon--outlined': !history.addedToCart,
                        }"
                      >
                        {{ history.addedToCart ? '追加' : '–' }}
                      </span>
                    </div>

                    <!-- Purchased -->
                    <div class="oct-salon-customer-list__col">
                      <span class="oct-salon-customer-list__icon"
                        :class="{
                          'oct-salon-customer-list__icon--outlined': !history.purchased,
                        }"
                      >
                        {{ history.purchased ? '購入' : '–' }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

        </div>

        <!-- Inset Shadow (only when horizontal overflow / scroll is possible) -->
        <div
          v-show="$data.$_horizontalOverflow_isOverflowing"
          class="oct-salon-customer-list__shadow"
        />
      </div>
    </div>

    <!-- Child routes -->
    <transition :name="transitionName">
      <router-view />
    </transition>
  </div>
</template>

<script>
import { getExecutionEnvironment } from '@/plugins/AppConfig';
import API from '@/api/index.js';
import CustomerRepository from '@/core/infrastructure/CustomerRepository';
import OctIcon from '@/components/icon/OctIcon.vue';
import OctTextField from '@/components/text-field/OctTextField.vue';
import OctSelect from '@/components/select/OctSelect.vue';
import OctMenu from '@/components/menu/OctMenu.vue';
import OctMenuItem from '@/components/menu-item/OctMenuItem.vue';
import OctMenuList from '@/components/menu-list/OctMenuList.vue';
import OctMenuItemGroup from '@/components/menu-item-group/OctMenuItemGroup.vue';
import OctPopover from '@/components/popover/OctPopover.vue';
import DashboardQueryService from '@/core/infrastructure/DashboardQueryService';
import { createHorizontalOverflowMixin } from '@/mixins/OctHorizontalOverflowMixin.js';
import CustomerMapper from './CustomerMapper';
import TreatmentHistoryMapper from './TreatmentHistoryMapper';

export default {
  name: 'OctSalonCustomerList',
  mixins: [
    createHorizontalOverflowMixin({
      scrollRef: 'octSclScrollContainer',
    }),
  ],
  components: {
    OctIcon,
    OctTextField,
    OctSelect,
    OctMenu,
    OctMenuItem,
    OctMenuList,
    OctMenuItemGroup,
    OctPopover,
  },
  props: {
    salonId: {
      type: String,
      default: '',
    },
    startDate: {
      type: String,
      default: '',
    },
    endDate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      /** @type {DashboardQueryService|null} */
      $_octSCL_dashboardQueryService: null,
      /**
       * Customer rows with performance
       * 
       * @type {Array<{
       *   customerId: string,
       *   name: string,
       *   stylistNames: string[],
       *   lastVisitedAt: Date,
       *   hasPurchased: boolean,
       *   performance: {
       *     salesAmount: number,
       *     sentStyleCount: number,
       *     viewedStyleCount: number,
       *     favoriteCount: number,
       *     addToCartCount: number,
       *     purchaseCount: number,
       *   },
       *   history: Array<{
       *     stylistNames: string[],
       *     sentAt: Date,
       *     hasPurchased: boolean,
       *     salesAmount: number,
       *     viewed: boolean,
       *     favorited: boolean,
       *     addedToCart: boolean,
       *     purchased: boolean,
       *   }>,
       *   expanded: boolean,
       *   processing: boolean,
       * }>} Customer rows view model
       * @default []
       */
      $_octSCL_customers: [],
      /**
       * Indicate processing
       * @type {boolean}
       * @default false
       */
      $_octSCL_processing: false,
      /**
       * Sort by field
       * @type {string}
       * @default 'lastVisitedAt'
       */
      $_octSCL_sort: 'lastVisitedAt',
      /**
       * Sort order
       * @type {'asc'|'desc'}
       * @default 'desc'
       */
      $_octSCL_order: 'desc',
      /**
       * Limit of customers to fetch
       * @type {number}
       * @default 100
       */
      $_octSCL_limit: 100,
      /**
       * Search word
       * @type {string}
       * @default ''
       */
      $_octSCL_searchWord: '',
      /**
       * Comma-separated membership card numbers to narrow the dashboard query; `null` when unset.
       * @type {string|null}
       * @default null
       */
      $_octSCL_customerIds: null,
    };
  },
  computed: {
    /**
     * Stable key combining `startDate` and `endDate` so a single watcher can
     * refetch when either changes, without double-firing when both update in one tick.
     */
    $_octSCL_dateRangeKey() {
      return `${this.startDate}|${this.endDate}`;
    },
    /**
     * Header row after customer name: plain `label` only when `sortKey` is omitted; otherwise label + sort menu
     * (`sort` query keys for {@link DashboardQueryService.getCustomers}).
     * @returns {Array<{ label: string, sortKey?: string, menuPlacement?: 'left'|'right' }>}
     */
    $_octSCL_headerColumns() {
      return [
        { label: 'スタイリスト名', sortKey: 'stylistNames' },
        { label: '最終来店日', sortKey: 'lastVisitedAt' },
        { label: '購入歴' },
        { label: '売上', sortKey: 'salesAmount' },
        { label: '送信', sortKey: 'sentStyleCount' },
        { label: '閲覧', sortKey: 'viewedStyleCount' },
        { label: '♥', sortKey: 'favoriteCount', menuPlacement: 'left' },
        { label: 'カート', sortKey: 'addToCartCount', menuPlacement: 'left' },
        { label: '購入', sortKey: 'purchaseCount', menuPlacement: 'left' },
      ];
    },
    /** Transition name */
    transitionName: vm => vm.$store.state.session.history.action,
  },
  methods: {
    /**
     * Loads the salon customer list for the current `salonId` and date range,
     * updating `$_octSCL_customers` and toggling `$_octSCL_processing`.
     * Dispatches a global alert if the request fails.
     *
     * @async
     * @returns {Promise<void>}
     */
    async $_octSCL_update() {
      this.$data.$_octSCL_processing = true;

      try {
        this.$data.$_octSCL_customers = await this.$_octSCL_fetchCustomers(
          this.$data.$_octSCL_dashboardQueryService,
          this.salonId,
          this.$data.$_octSCL_customerIds,
          this.startDate,
          this.endDate,
        ).then(async (response) => {
          const customerIds = response.map(customer => customer.customerId);
          const customerRepository = new CustomerRepository(API);

          // Fetch customer names by IDs
          const customers = await customerRepository.findByIds(customerIds);
          // Create a map of customer IDs to customer names
          const customerMap = new Map(customers.items.map(customer => [customer.id, customer]));

          // Merge customer name into response
          return response.map(item => {
            const customer = customerMap.get(item.customerId);
            return {
              ...item,
              name: customer ? customer.name : '',
            };
          });
        });
      } catch (error) {
        console.error(error);
        this.$store.dispatch('alert', '顧客一覧を取得できませんでした');
      } finally {
        this.$data.$_octSCL_processing = false;
      }
    },
    /**
     * Splits a search phrase into given/family name parts.
     * A single token is treated as family name only; two or more tokens use the first as family name
     * and the rest as given name (e.g. `内山 可奈子` → lastName `内山`, firstName `可奈子`).
     *
     * @param {string} searchWord
     * @returns {{ firstName: string, lastName: string }}
     */
    $_octSCL_parseSearchName(searchWord) {
      const parts = searchWord.trim().split(/\s+/).filter(Boolean);

      if (parts.length <= 1) {
        return { firstName: '', lastName: parts[0] || '' };
      }

      return {
        firstName: parts.slice(1).join(' '),
        lastName: parts[0],
      };
    },
    /**
     * Resolves membership card numbers from the search field, updates `$_octSCL_customerIds`,
     * then reloads the salon customer list.
     *
     * @async
     * @returns {Promise<void>}
     */
    async $_octSCL_searchByCustomerName() {
      const searchWord = (this.$data.$_octSCL_searchWord || '').trim();

      try {
        if (!searchWord) {
          this.$data.$_octSCL_customerIds = null;
        } else {
          this.$data.$_octSCL_processing = true;

          const { firstName, lastName } = this.$_octSCL_parseSearchName(searchWord);
          const { items } = await new CustomerRepository(API).findByName(firstName, lastName);

          if (!items.length) {
            this.$data.$_octSCL_customerIds = null;
            this.$data.$_octSCL_customers = [];
            return;
          }

          this.$data.$_octSCL_customerIds = items.map(item => item.id).join(',');
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch('alert', '顧客検索に失敗しました');
        return;
      } finally {
        this.$data.$_octSCL_processing = false;
      }

      await this.$_octSCL_update();
    },
    /**
     * Fetches customers from the dashboard query service and maps each row to the list view model.
     *
     * @async
     * @param {string} salonId Target salon identifier.
     * @param {string} startDate Range start (`YYYY-MM-DD`).
     * @param {string} endDate Range end (`YYYY-MM-DD`).
     * @returns {Promise<Array<Object>>} One mapped row per customer (see CustomerMapper.toViewModel).
     */
    async $_octSCL_fetchCustomers(queryService, salonId, customerIds, startDate, endDate) {
      const response = await queryService.getCustomers(
        salonId,
        startDate,
        endDate,
        customerIds || null,
        this.$data.$_octSCL_sort,
        this.$data.$_octSCL_order,
        this.$data.$_octSCL_limit,
      );

      return response.map(customer => CustomerMapper.toViewModel(customer));
    },
    /**
     * Handles a click on a customer row: toggles visibility when history is already loaded
     * or the section is open; otherwise loads treatment history then toggles open.
     * No-op while `item.processing` is true. Dispatches a global alert if the history request fails.
     *
     * @async
     * @param {Object} item Customer row from `$_octSCL_customers` (mutated in place).
     * @returns {Promise<void>}
     */
    async $_octSCL_onClickCustomer(item) {
      if (item.processing) return;

      if (item.expanded || item.history.length > 0)
        return this.$_octSCL_toggleTreatmentHistory(item);

      try {
        item.processing = true;
        item.history = await this.$_octSCL_fetchTreatmentHistory(
          this.$data.$_octSCL_dashboardQueryService,
          this.salonId,
          item.customerId,
          this.startDate,
          this.endDate,
        );
        this.$_octSCL_toggleTreatmentHistory(item);
      } catch (error) {
        console.error(error);
        this.$store.dispatch('alert', '施術履歴を取得できませんでした');
      } finally {
        item.processing = false;
      }
    },
    /**
     * Flips `item.expanded` to show or hide treatment-history rows beneath the customer line.
     *
     * @param {Object} item Customer row from `$_octSCL_customers` (mutated in place).
     * @returns {void}
     */
    $_octSCL_toggleTreatmentHistory(item) {
      item.expanded = !item.expanded;
    },
    /**
     * Loads per-send treatment history for one customer in the current date range
     * and maps each API row with `TreatmentHistoryMapper.toViewModel`.
     *
     * @async
     * @param {string} salonId Target salon identifier.
     * @param {string} customerId Customer to load history for.
     * @param {string} startDate Range start (`YYYY-MM-DD`).
     * @param {string} endDate Range end (`YYYY-MM-DD`).
     * @returns {Promise<Array<Object>>} Mapped treatment rows for the expanded sub-list.
     */
    async $_octSCL_fetchTreatmentHistory(queryService, salonId, customerId, startDate, endDate) {
      return await queryService.getTreatmentHistory(
        salonId,
        customerId,
        startDate,
        endDate
      ).then(response => response.map(item => TreatmentHistoryMapper.toViewModel(item)));
    },
    /**
     * Format a sales amount as a Japanese yen currency string.
     * @param {number} amount - Sales amount in JPY.
     * @returns {string} Localized currency string (e.g. "￥344,740").
     */
    $_octSCL_formatSalesAmount(amount) {
      return amount.toLocaleString('ja-JP', { style: 'currency', currency: 'JPY' });
    },
    /**
     * @param {{ menuPlacement?: 'left'|'right' }} col
     * @returns {{ vertical: string, horizontal: string }}
     */
    $_octSCL_popoverAnchorOrigin(col) {
      if (col.menuPlacement === 'left') {
        return { vertical: 'top', horizontal: 'left' };
      }
      return { vertical: 'top', horizontal: 'right' };
    },
    /**
     * @param {{ menuPlacement?: 'left'|'right' }} col
     * @returns {{ vertical: string, horizontal: string }}
     */
    $_octSCL_popoverTransformOrigin(col) {
      if (col.menuPlacement === 'left') {
        return { vertical: 'top', horizontal: 'right' };
      }
      return { vertical: 'top', horizontal: 'left' };
    },
    /**
     * @param {string} sortKey
     * @param {'asc'|'desc'} order
     * @returns {Promise<void>}
     */
    async $_octSCL_setSort(sortKey, order) {
      this.$data.$_octSCL_sort = sortKey;
      this.$data.$_octSCL_order = order;
      await this.$_octSCL_update();
    },
    /** Restore default API sort (last visit date, descending). */
    async $_octSCL_resetCustomerSort() {
      this.$data.$_octSCL_sort = 'lastVisitedAt';
      this.$data.$_octSCL_order = 'desc';
      await this.$_octSCL_update();
    },
    /**
     * @param {string} columnSortKey
     * @returns {boolean}
     */
    $_octSCL_isSortResetDisabled(columnSortKey) {
      const sort = this.$data.$_octSCL_sort;
      const order = this.$data.$_octSCL_order;
      if (sort !== columnSortKey) return true;
      return sort === 'lastVisitedAt' && order === 'desc';
    },
  },
  mounted() {
    this.$data.$_octSCL_dashboardQueryService = new DashboardQueryService(
      this.$appConfig().apiBase,
      getExecutionEnvironment().isProduction() ? API.getAccessToken() : null,
    );

    this.$_octSCL_update();
  },
  watch: {
    $_octSCL_dateRangeKey: {
      handler() {
        this.$_octSCL_update();
      },
    },
    '$data.$_octSCL_processing': {
      handler(isProcessing) {
        if (!isProcessing) {
          this.$nextTick(() => this.$_horizontalOverflow_sync());
        }
      },
    },
  },
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-salon-customer-list {
  display: flex;
  flex-direction: column;
  gap: oct-rem(32);
}

.oct-salon-customer-list-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: oct-rem(16);
  background-color: white;
}

.oct-salon-customer-list-header__title {
  font-size: oct-rem(20);
  line-height: 1;
  font-weight: bold;
}

.oct-salon-customer-list__wrapper {
  position: relative;
}

.oct-salon-customer-list__container {
  min-height: 100dvh;

  &--has-horizontal-overflow {
    overflow-x: auto;
  }
}

.oct-salon-customer-list__shadow {
  position: absolute;
  top: 0;
  right: 0;
  width: oct-rem(32);
  height: 100%;
  background-color: rgba(255, 255, 255, 0.00);
  box-shadow: -20px 0 20px -16px rgba(0, 0, 0, 0.10) inset;
}

.oct-salon-customer-list__row {
  display: grid;
  grid-template-columns: oct-rem(16)  oct-rem(144) oct-rem(144) oct-rem(120) oct-rem(72) oct-rem(96) minmax(oct-rem(72), 1fr) minmax(oct-rem(72), 1fr) minmax(oct-rem(72), 1fr) minmax(oct-rem(72), 1fr) minmax(oct-rem(72), 1fr);
  gap: oct-rem(32);
  width: 100%;
  min-width: max-content;
  padding: 0 oct-rem(32);
  border-top: oct-rem(1) solid $oct-theme--neutral-20;

  &--customer {
    cursor: pointer;
  }

  &--treatment {
    background-color: $oct-theme--neutral-10;
  }
}

.oct-salon-customer-list__header {
  position: sticky;
  top: oct-rem(120);
  border-top-width: 0;
  background-color: $oct-theme--surface;

  .oct-salon-customer-list__container--has-horizontal-overflow & {
    position: static;
    top: auto;
  }
}

.oct-salon-customer-list__col {
  font-size: oct-rem(14);
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: oct-rem(48);
  overflow: hidden;

  &--header {
    overflow: visible;
  }

  .oct-salon-customer-list__header & {
    font-size: oct-rem(12);
    color: $oct-theme--neutral-50;
  }
}

.oct-salon-customer-list__header-label {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: oct-rem(6);
}

.oct-salon-customer-list-header__filters {
  display: flex;
  align-items: center;
  gap: oct-rem(16);

  ::v-deep .oct-text-field {
    width: oct-rem(240);
  }

  ::v-deep .oct-select__native-input {
    height: oct-rem(40);
  }
}

.oct-salon-customer-list__filter-icon {
  fill: $oct-theme--neutral-50;
  width: oct-rem(16);
  height: oct-rem(16);
  cursor: pointer;
}

.oct-salon-customer-list__menu {
  min-width: oct-rem(240);
  margin-left: oct-rem(16);

  &--popover-left {
    margin-left: 0;
    margin-right: oct-rem(6);
  }
}

.oct-salon-customer-list__text {
  text-align: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  overflow: hidden;
}

.oct-salon-customer-list__icon {
  font-size: oct-rem(10);
  color: $oct-theme--on-primary;
  line-height: 1;
  display: flex;
  justify-content: center;
  padding: oct-rem(8);
  background-color: $oct-theme--primary;
  border-radius: oct-rem(13);
  min-width: oct-rem(36);

  &--outlined {
    color: $oct-theme--neutral-30;
    padding: oct-rem(7);
    background-color: transparent;
    border: oct-rem(1) solid $oct-theme--neutral-30;
  }
}

.oct-salon-customer-list__handle {
  fill: black;
  width: oct-rem(16);
  height: oct-rem(16);
}
</style>
